// =====================================================
// CKANSyncPanel
// Descripción: Panel de administración para sincronizar datos CKAN
// =====================================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAllSyncStatus,
  syncCKANMetadata,
  syncDataFile,
  downloadShapefile,
  processShapefile,
  processDataFileInBrowser,
  syncAllCKAN,
  discoverCKANResources,
  updateSyncStatus,
  type CKANSyncStatus,
} from '@/lib/ckan/ckan-storage';
import { Database, Download, FileJson, MapPin, TrendingUp, RefreshCw, CheckCircle, XCircle, Loader2, AlertCircle, Zap, Search, HardDrive, Cloud, AlertTriangle, Wifi, WifiOff, Play } from 'lucide-react';
import { CKAN_CONFIG } from '@/lib/ckan/ckan-config';
import { checkCKANHealth, getHealthStatusMessage, getHealthStatusColor, type CKANHealthStatus } from '@/lib/ckan/ckan-health';
import { ErrorModal } from './ErrorModal';
import { ProcessingProgressModal } from './ProcessingProgressModal';

// Helper: Format file size
function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes === 0) return 'N/A';

  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// Helper: Get file size color indicator
function getFileSizeColor(bytes: number | null | undefined): string {
  if (!bytes) return 'bg-gray-400'; // Unknown size

  const kb = bytes / 1024;
  const mb = bytes / (1024 * 1024);

  if (kb < 300) return 'bg-green-500'; // < 300 KB
  if (mb < 5) return 'bg-yellow-500';  // 300 KB - 5 MB
  if (mb < 20) return 'bg-orange-500'; // 5 MB - 20 MB
  return 'bg-red-500';                 // > 20 MB
}

export function CKANSyncPanel() {
  const queryClient = useQueryClient();
  const [processingStatus, setProcessingStatus] = useState<string>('');
  const [healthStatus, setHealthStatus] = useState<CKANHealthStatus | null>(null);

  // Estado para el modal de progreso
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [processingResource, setProcessingResource] = useState('');
  const [progressLogs, setProgressLogs] = useState<string[]>([]);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  const [errorModalData, setErrorModalData] = useState<{ isOpen: boolean; errors: string[]; successCount: number; totalCount: number }>({
    isOpen: false,
    errors: [],
    successCount: 0,
    totalCount: 0,
  });

  // Query: Obtener estados de sincronización
  const { data: syncStatuses, isLoading } = useQuery({
    queryKey: ['ckan-sync-status'],
    queryFn: getAllSyncStatus,
    refetchInterval: 5000, // Refetch cada 5 segundos para updates en tiempo real
  });

  // Mutation: Sincronizar metadatos
  const metadataMutation = useMutation({
    mutationFn: syncCKANMetadata,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ckan-sync-status'] });
    },
  });

  // Mutation: Sincronizar archivos de datos (CSV, Excel, JSON, XML)
  const dataFileMutation = useMutation({
    mutationFn: ({ resourceName, fileType }: { resourceName: string; fileType: 'csv' | 'excel' | 'json' | 'xml' }) =>
      syncDataFile(resourceName, fileType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ckan-sync-status'] });
    },
  });

  // Mutation: Descargar Shapefile
  const shapefileMutation = useMutation({
    mutationFn: (resourceName: string) => downloadShapefile(resourceName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ckan-sync-status'] });
    },
  });

  // Mutation: Sincronizar todos
  const syncAllMutation = useMutation({
    mutationFn: (types: string[]) => syncAllCKAN(types),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ckan-sync-status'] });
      

      // Si hay errores, mostrar el modal
      if (data.stats && data.stats.errors && data.stats.errors.length > 0) {
        const totalSuccessCount =
          (data.stats.csv_success || 0) +
          (data.stats.excel_success || 0) +
          (data.stats.json_success || 0) +
          (data.stats.xml_success || 0);

        setErrorModalData({
          isOpen: true,
          errors: data.stats.errors,
          successCount: totalSuccessCount,
          totalCount: data.stats.total,
        });
      }
    },
  });

  // Mutation: Descubrir recursos desde CKAN
  const discoverMutation = useMutation({
    mutationFn: discoverCKANResources,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ckan-sync-status'] });
      alert(`✅ Descubiertos: ${data.stats.new} nuevos recursos\n📊 Total: ${data.stats.total}\n📄 Archivos de datos (CSV/Excel/JSON/XML): ${data.stats.data_files || data.stats.csvs}\n🗺️ Shapefiles: ${data.stats.shapefiles}`);
    },
    onError: (error) => {
      alert(`❌ Error al descubrir recursos: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    },
  });

  // Función: Verificar salud del servidor CKAN
  const handleCheckHealth = async () => {
    setIsCheckingHealth(true);
    try {
      const health = await checkCKANHealth();
      setHealthStatus(health);
    } finally {
      setIsCheckingHealth(false);
    }
  };

  // Función: Procesar Shapefile (no es mutation porque usa Web Worker)
  const handleProcessShapefile = async (resourceName: string) => {
    try {
      // Abrir modal y resetear logs
      setProcessingResource(resourceName);
      setProgressLogs([]);
      setShowProgressModal(true);
      setProcessingStatus('Iniciando procesamiento...');
      

      await processShapefile(resourceName, (status) => {
        setProcessingStatus(status);
        // Agregar log al modal
        setProgressLogs(prev => [...prev, status]);
      });

      // Agregar log de éxito
      setProgressLogs(prev => [...prev, '✅ Procesamiento completado exitosamente']);

      queryClient.invalidateQueries({ queryKey: ['ckan-sync-status'] });
      setProcessingStatus('');

      // Cerrar modal después de 3 segundos
      setTimeout(() => {
        setShowProgressModal(false);
      }, 30000);
    } catch (error) {
      console.error('Error processing shapefile:', error);
      setProgressLogs(prev => [...prev, `❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`]);
      setProcessingStatus('');
    }
  };

  // Handler: Procesar archivo de datos (CSV/Excel/JSON/XML) en browser
  const handleProcessDataFile = async (resourceName: string, fileType: 'csv' | 'excel' | 'json' | 'xml') => {
    try {
      setProcessingStatus('Iniciando procesamiento en navegador...');

      const result = await processDataFileInBrowser(resourceName, fileType, (status, progress) => {
        setProcessingStatus(`${status}${progress ? ` (${progress}%)` : ''}`);
      });

      // WORKAROUND: Update status manually since Worker might fail due to RLS
      console.log('[Frontend] Updating status manually after Worker success');
      try {
        await updateSyncStatus(resourceName, {
          status: 'ready',
          ready_to_process: false,
          processed_at: new Date().toISOString(),
          storage_strategy: result.storage_strategy,
          storage_path_data: result.storage_path_data || null,
          records_count: result.records_count,
        });
        console.log('[Frontend] Status updated successfully');
      } catch (updateError) {
        console.error('[Frontend] Failed to update status:', updateError);
      }

      // Show success message
      setProcessingStatus(`✅ Procesamiento exitoso: ${result.records_count} registros (${result.storage_strategy})`);

      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['ckan-sync-status'] });

      // Clear message after 3 seconds
      setTimeout(() => setProcessingStatus(''), 3000);
    } catch (error) {
      console.error('Error processing data file:', error);
      setProcessingStatus('');
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Cargando estado de sincronizaciones...</span>
      </div>
    );
  }

  // Agrupar por tipo
  const metadataResources = syncStatuses?.filter(s => s.resource_type === 'metadata') || [];
  // Sort by file size (ascending)
  const dataResources = (syncStatuses?.filter(s => ['csv', 'excel', 'json', 'xml'].includes(s.resource_type)) || [])
    .sort((a, b) => (a.file_size_bytes || 0) - (b.file_size_bytes || 0));

  const shapefileResources = (syncStatuses?.filter(s => s.resource_type === 'shapefile') || [])
    .sort((a, b) => (a.file_size_bytes || 0) - (b.file_size_bytes || 0));

  // Calcular estadísticas
  const totalResources = syncStatuses?.length || 0;
  const readyCount = syncStatuses?.filter(s => s.status === 'ready').length || 0;
  const downloadingCount = syncStatuses?.filter(s => s.status === 'downloading' || s.status === 'processing').length || 0;
  const pendingCount = syncStatuses?.filter(s => s.status === 'idle' || s.status === 'downloaded').length || 0;
  const errorCount = syncStatuses?.filter(s => s.status === 'error').length || 0;

  // Calcular cuántos faltan por completar (pendientes + sincronizando + errores)
  const remainingCount = totalResources - readyCount;

  // Calcular progreso
  const progressPercentage = totalResources > 0 ? Math.round((readyCount / totalResources) * 100) : 0;

  return (
    <>
    <div className="p-6 space-y-6">
      {/* Warning Banner: Frontend Mode */}
      {CKAN_CONFIG.MODE === 'frontend' && (
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-bold text-yellow-900 mb-1">Modo Frontend Activo</h3>
            <p className="text-sm text-yellow-800">
              Las llamadas a CKAN se están haciendo directamente desde tu navegador (no desde Edge Functions).
              Esto funciona porque estás en la misma red que el servidor CKAN.
            </p>
            <p className="text-xs text-yellow-700 mt-2">
              <strong>Nota:</strong> Para producción, cambia <code className="bg-yellow-100 px-1 rounded">CKAN_CONFIG.MODE</code> a <code className="bg-yellow-100 px-1 rounded">'edge-functions'</code> en <code className="bg-yellow-100 px-1 rounded">src/lib/ckan/ckan-config.ts</code>
            </p>
          </div>
        </div>
      )}

      {/* Server Health Status Banner */}
      {CKAN_CONFIG.MODE === 'frontend' && (
        <div className="border-2 rounded-lg p-4 flex items-start gap-3">
          <div className="flex-1 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {healthStatus ? (
                healthStatus.isAvailable ? (
                  <Wifi className="w-6 h-6 text-green-600 flex-shrink-0" />
                ) : (
                  <WifiOff className="w-6 h-6 text-red-600 flex-shrink-0" />
                )
              ) : (
                <Wifi className="w-6 h-6 text-gray-400 flex-shrink-0" />
              )}

              <div>
                <h3 className="font-bold text-gray-900 mb-1">Estado del Servidor CKAN</h3>
                {healthStatus ? (
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-md border ${getHealthStatusColor(healthStatus)}`}>
                      {getHealthStatusMessage(healthStatus)}
                    </span>
                    <span className="text-xs text-gray-500">
                      Última verificación: {healthStatus.lastChecked.toLocaleTimeString()}
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    Verifica si el servidor CKAN está disponible antes de sincronizar
                  </p>
                )}
              </div>
            </div>

            <button
              onClick={handleCheckHealth}
              disabled={isCheckingHealth}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
            >
              {isCheckingHealth ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verificando...
                </>
              ) : (
                <>
                  <Wifi className="w-4 h-4" />
                  Probar Conexión
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Database className="w-8 h-8" />
              Sincronización CKAN
            </h1>
            <p className="text-muted-foreground mt-2">
              Gestiona la sincronización de datos desde el CKAN API de Diputación de Palencia
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {/* Discover Resources Button */}
            <button
              onClick={() => discoverMutation.mutate()}
              disabled={discoverMutation.isPending}
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold shadow-lg transition-all"
            >
              {discoverMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Descubriendo...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  Descubrir Recursos
                </>
              )}
            </button>

            {/* Sync All Button */}
            <button
              onClick={() => syncAllMutation.mutate(['csv', 'excel', 'json', 'xml'])}
              disabled={syncAllMutation.isPending || downloadingCount > 0 || remainingCount === 0}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold shadow-lg transition-all"
            >
              {syncAllMutation.isPending || downloadingCount > 0 ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sincronizando... ({readyCount}/{totalResources})
                </>
              ) : remainingCount === 0 ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Todo Sincronizado
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  Sincronizar Todos ({pendingCount} pendientes)
                </>
              )}
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4 space-y-3">
          {/* Barra de progreso visual */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Progreso de Sincronización
              </span>
              <span className="text-sm font-bold text-gray-900">
                {readyCount} / {totalResources} ({progressPercentage}%)
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className="bg-gradient-to-r from-green-500 to-green-600 h-4 rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-2"
                style={{ width: `${progressPercentage}%` }}
              >
                {progressPercentage > 5 && (
                  <span className="text-xs font-bold text-white">
                    {progressPercentage}%
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div className="text-2xl font-bold text-green-700">{readyCount}</div>
              </div>
              <div className="text-xs text-green-600 mt-1">Completados</div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <div className="text-2xl font-bold text-blue-700">{downloadingCount}</div>
              </div>
              <div className="text-xs text-blue-600 mt-1">Sincronizando</div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <div className="text-2xl font-bold text-yellow-700">{pendingCount}</div>
              </div>
              <div className="text-xs text-yellow-600 mt-1">Pendientes</div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <XCircle className="w-5 h-5 text-red-600" />
                <div className="text-2xl font-bold text-red-700">{errorCount}</div>
              </div>
              <div className="text-xs text-red-600 mt-1">Errores</div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Sync Status - Mostrar recursos que se están sincronizando */}
      {downloadingCount > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-5 shadow-md">
          <div className="flex items-center gap-3 mb-4">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            <div>
              <h3 className="text-lg font-bold text-blue-900">
                Sincronización en Progreso
              </h3>
              <p className="text-sm text-blue-700">
                {downloadingCount} {downloadingCount === 1 ? 'recurso' : 'recursos'} sincronizándose...
              </p>
            </div>
          </div>

          {/* Lista de recursos sincronizándose */}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {syncStatuses
              ?.filter(s => s.status === 'downloading' || s.status === 'processing')
              .map(resource => (
                <div
                  key={resource.id}
                  className="bg-white rounded-md p-3 flex items-center justify-between border border-blue-200"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {resource.resource_label}
                      </p>
                      <p className="text-xs text-gray-500">
                        {resource.status === 'downloading' ? 'Descargando y procesando...' : 'Procesando datos...'}
                      </p>
                    </div>
                  </div>
                  {resource.records_count && (
                    <span className="text-xs text-gray-500 ml-2">
                      {resource.records_count} registros
                    </span>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Processing Status (Shapefiles) */}
      {processingStatus && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <span className="text-blue-900 font-medium">{processingStatus}</span>
        </div>
      )}

      {/* Sección 1: Metadatos */}
      <div className="bg-card border rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <FileJson className="w-5 h-5" />
          Metadatos de Datasets
        </h2>
        <div className="space-y-3">
          {metadataResources.map(resource => (
            <SyncResourceCard
              key={resource.id}
              resource={resource}
              onSync={() => metadataMutation.mutate()}
              isLoading={metadataMutation.isPending}
            />
          ))}
        </div>
      </div>

      {/* Sección 2: Datos Tabulares (CSV, Excel, JSON, XML) */}
      <div className="bg-card border rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Database className="w-5 h-5" />
          Datos Tabulares (CSV, Excel, JSON, XML)
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Archivos de datos en múltiples formatos. Se sincronizan automáticamente usando Edge Functions.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {dataResources.map(resource => (
            <SyncResourceCard
              key={resource.id}
              resource={resource}
              onSync={() => dataFileMutation.mutate({
                resourceName: resource.resource_name,
                fileType: resource.resource_type as 'csv' | 'excel' | 'json' | 'xml'
              })}
              onProcess={() => handleProcessDataFile(resource.resource_name, resource.resource_type as 'csv' | 'excel' | 'json' | 'xml')}
              isLoading={dataFileMutation.isPending}
              isProcessing={processingStatus !== ''}
            />
          ))}
        </div>
      </div>

      {/* Sección 3: Shapefiles */}
      <div className="bg-card border rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5" />
          Datos Geográficos (Shapefiles)
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Los shapefiles requieren 2 pasos: (1) Descargar desde CKAN, (2) Procesar en tu navegador para convertir a GeoJSON.
        </p>
        <div className="space-y-4">
          {shapefileResources.map(resource => (
            <ShapefileResourceCard
              key={resource.id}
              resource={resource}
              onDownload={() => shapefileMutation.mutate(resource.resource_name)}
              onProcess={() => handleProcessShapefile(resource.resource_name)}
              isDownloading={shapefileMutation.isPending}
              isProcessing={processingStatus !== ''}
            />
          ))}
        </div>
      </div>

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModalData.isOpen}
        onClose={() => setErrorModalData({ ...errorModalData, isOpen: false })}
        errors={errorModalData.errors}
        successCount={errorModalData.successCount}
        totalCount={errorModalData.totalCount}
      />
    </div>

    {/* Modal de progreso de procesamiento */}
    <ProcessingProgressModal
      isOpen={showProgressModal}
      onClose={() => setShowProgressModal(false)}
      resourceName={processingResource}
      logs={progressLogs}
    />
    </>
  );
}

// =====================================================
// SyncResourceCard: Recurso simple (CSV/Metadata/JSON/XML) con 2 pasos opcionales
// =====================================================
interface SyncResourceCardProps {
  resource: CKANSyncStatus;
  onSync: () => void;
  onProcess?: () => void;
  isLoading: boolean;
  isProcessing?: boolean;
}

function SyncResourceCard({ resource, onSync, onProcess, isLoading, isProcessing = false }: SyncResourceCardProps) {
  const statusIcon = getStatusIcon(resource.status);
  const statusColor = getStatusColor(resource.status);

  // Check if this is a data file (CSV/Excel/JSON/XML) with 2-step processing
  const isTwoStepResource = resource.resource_type && ['csv', 'excel', 'json', 'xml'].includes(resource.resource_type);
  const canProcess = resource.status === 'downloaded' && resource.ready_to_process;
  const isReady = resource.status === 'ready' && !resource.ready_to_process;

  return (
    <div className={`border rounded-lg p-4 transition-all ${
      resource.status === 'downloading' || resource.status === 'processing'
        ? 'bg-blue-50 border-blue-300 shadow-md'
        : resource.status === 'ready'
        ? 'bg-green-50 border-green-300'
        : resource.status === 'downloaded' && resource.ready_to_process
        ? 'bg-yellow-50 border-yellow-300'
        : resource.status === 'error'
        ? 'bg-red-50 border-red-300'
        : 'hover:bg-accent/50'
    }`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-medium">{resource.resource_label}</h3>

            {/* File Type Badge */}
            {resource.resource_type && resource.resource_type !== 'metadata' && (
              <span className={`px-2 py-0.5 text-xs font-bold rounded-md ${
                resource.resource_type === 'csv' ? 'bg-green-100 text-green-700 border border-green-300' :
                resource.resource_type === 'excel' ? 'bg-emerald-100 text-emerald-700 border border-emerald-300' :
                resource.resource_type === 'json' ? 'bg-orange-100 text-orange-700 border border-orange-300' :
                resource.resource_type === 'xml' ? 'bg-amber-100 text-amber-700 border border-amber-300' :
                'bg-gray-100 text-gray-700 border border-gray-300'
              }`}>
                {resource.resource_type.toUpperCase()}
              </span>
            )}

            {resource.status === 'downloading' && (
              <span className="px-2 py-0.5 bg-blue-200 text-blue-800 text-xs font-bold rounded-full animate-pulse">
                ACTIVO
              </span>
            )}

            {/* Storage Strategy Badge */}
            {resource.storage_strategy && (
              <span className={`px-2 py-0.5 text-xs font-medium rounded-md flex items-center gap-1 ${
                resource.storage_strategy === 'postgresql'
                  ? 'bg-purple-100 text-purple-700 border border-purple-300'
                  : 'bg-sky-100 text-sky-700 border border-sky-300'
              }`}>
                {resource.storage_strategy === 'postgresql' ? (
                  <><HardDrive className="w-3 h-3" /> PostgreSQL</>
                ) : (
                  <><Cloud className="w-3 h-3" /> Storage</>
                )}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-1">
            <span className={`text-sm font-medium ${statusColor} flex items-center gap-1`}>
              {statusIcon}
              <span className="capitalize">
                {resource.status === 'idle' ? 'Pendiente' :
                 resource.status === 'downloading' ? 'Sincronizando' :
                 resource.status === 'ready' ? 'Completado' :
                 resource.status === 'error' ? 'Error' :
                 resource.status}
              </span>
            </span>
            {resource.records_count !== null && (
              <span className="text-xs text-muted-foreground">
                • {resource.records_count} registros
              </span>
            )}
            {resource.file_size_bytes && (
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                • <span className={`w-2 h-2 rounded-full ${getFileSizeColor(resource.file_size_bytes)}`}></span>
                {formatFileSize(resource.file_size_bytes)}
              </span>
            )}
          </div>

          {/* Mini Progress Bar for active downloads */}
          {resource.status === 'downloading' && (
            <div className="mt-2 w-full bg-blue-200 rounded-full h-2 overflow-hidden">
              <div className="bg-blue-600 h-2 rounded-full animate-pulse w-full"></div>
            </div>
          )}

          {resource.last_sync_completed_at && (
            <p className="text-xs text-muted-foreground mt-1">
              Última sincronización: {new Date(resource.last_sync_completed_at).toLocaleString('es-ES')}
            </p>
          )}
          {resource.last_error && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold mb-1">Error:</div>
                  <div className="whitespace-pre-line">{resource.last_error}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Botones: 1 o 2 según si es recurso con procesamiento en 2 pasos */}
        {isTwoStepResource && canProcess && onProcess ? (
          // Recursos de datos grandes (CSV/Excel/JSON/XML) con 2 pasos: Descargar + Procesar
          <div className="flex flex-col gap-2 min-w-[140px]">
            {/* Paso 1: Descargar (ya completado) */}
            <button
              disabled={true}
              className="px-4 py-2 bg-blue-600 text-white rounded-md opacity-50 cursor-not-allowed flex items-center justify-center gap-2 text-sm"
            >
              <CheckCircle className="w-4 h-4" />
              Descargado
            </button>

            {/* Paso 2: Procesar en Browser */}
            <button
              onClick={onProcess}
              disabled={isProcessing}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Procesar
                </>
              )}
            </button>
          </div>
        ) : (
          // Recursos simples (Metadata) o archivos pequeños (1 solo paso)
          <button
            onClick={onSync}
            disabled={isLoading || resource.status === 'downloading' || isReady}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
          >
            {(isLoading || resource.status === 'downloading') ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sincronizando...
              </>
            ) : isReady ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Completado
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Sincronizar
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// =====================================================
// ShapefileResourceCard: Recurso shapefile (2 pasos)
// =====================================================
interface ShapefileResourceCardProps {
  resource: CKANSyncStatus;
  onDownload: () => void;
  onProcess: () => void;
  isDownloading: boolean;
  isProcessing: boolean;
}

function ShapefileResourceCard({
  resource,
  onDownload,
  onProcess,
  isDownloading,
  isProcessing,
}: ShapefileResourceCardProps) {
  const statusIcon = getStatusIcon(resource.status);
  const statusColor = getStatusColor(resource.status);

  const canProcess = resource.status === 'downloaded' && resource.ready_to_process;
  const isReady = resource.status === 'ready' && !resource.ready_to_process;
  const requiresManualUpload = resource.status === 'requires_manual_upload';

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-medium">{resource.resource_label}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-sm ${statusColor}`}>{statusIcon} {resource.status}</span>
            {resource.file_size_bytes && (
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                • <span className={`w-2 h-2 rounded-full ${getFileSizeColor(resource.file_size_bytes)}`}></span>
                {formatFileSize(resource.file_size_bytes)}
              </span>
            )}
            {resource.records_count !== null && (
              <span className="text-xs text-muted-foreground">
                • {resource.records_count.toLocaleString()} features
              </span>
            )}
          </div>
          {resource.last_sync_completed_at && (
            <p className="text-xs text-muted-foreground mt-1">
              Última actualización: {new Date(resource.last_sync_completed_at).toLocaleString('es-ES')}
            </p>
          )}
          {resource.last_error && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold mb-1">Error:</div>
                  <div className="whitespace-pre-line">{resource.last_error}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Advertencia de carga manual para archivos grandes */}
      {requiresManualUpload && (
        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-blue-800">
              <div className="font-semibold mb-1">Archivo demasiado grande para el dashboard</div>
              <div className="mb-2">
                Este archivo shapefile ({formatFileSize(resource.file_size_bytes)}) supera el límite de 15 MB.
              </div>
              <div className="text-xs bg-blue-100 p-2 rounded border border-blue-300">
                <strong>Nota:</strong> Los archivos &gt;15MB generan GeoJSON de cientos de MB que no se pueden renderizar en el navegador.
                Estos recursos se procesarán en un proyecto dedicado con base de datos espacial y serán expuestos vía API para su consumo en el dashboard.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botones de acción */}
      <div className="flex gap-2">
        {requiresManualUpload ? (
          <div className="flex-1 px-4 py-2 bg-gray-100 text-gray-600 rounded-md flex items-center justify-center gap-2 text-sm cursor-not-allowed">
            <AlertTriangle className="w-4 h-4" />
            Procesamiento no disponible
          </div>
        ) : (
          <>
            {/* Paso 1: Descargar automáticamente */}
            <button
              onClick={onDownload}
              disabled={isDownloading || resource.status === 'downloading' || isReady || canProcess}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
            >
              {(isDownloading || resource.status === 'downloading') ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Descargando...
                </>
              ) : resource.status === 'idle' || resource.status === 'error' ? (
                <>
                  <Download className="w-4 h-4" />
                  1. Descargar
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Descargado
                </>
              )}
            </button>
          </>
        )}

        {/* Botón Procesar - SIEMPRE visible */}
        <button
          onClick={onProcess}
          disabled={!canProcess || isProcessing || isReady}
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
        >
          {isProcessing && resource.status === 'processing' ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Procesando...
            </>
          ) : isReady ? (
            <>
              <CheckCircle className="w-4 h-4" />
              Listo
            </>
          ) : (
            <>
              <TrendingUp className="w-4 h-4" />
              {requiresManualUpload ? '3. Procesar' : '2. Procesar'}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// =====================================================
// Helpers
// =====================================================
function getStatusIcon(status: string) {
  switch (status) {
    case 'idle':
      return '⏸️';
    case 'downloading':
      return <Loader2 className="w-4 h-4 animate-spin inline" />;
    case 'downloaded':
      return '⬇️';
    case 'processing':
      return <Loader2 className="w-4 h-4 animate-spin inline" />;
    case 'ready':
      return <CheckCircle className="w-4 h-4 inline text-green-600" />;
    case 'error':
      return <XCircle className="w-4 h-4 inline text-red-600" />;
    case 'requires_manual_upload':
      return <AlertTriangle className="w-4 h-4 inline text-yellow-600" />;
    default:
      return '❓';
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case 'idle':
      return 'text-gray-600';
    case 'downloading':
    case 'processing':
      return 'text-blue-600';
    case 'downloaded':
      return 'text-yellow-600';
    case 'ready':
      return 'text-green-600';
    case 'error':
      return 'text-red-600';
    case 'requires_manual_upload':
      return 'text-yellow-600';
    default:
      return 'text-gray-600';
  }
}
